/**
 * AdminStoreWaitlist — /admin/store-waitlist
 *
 * Lists everyone on `store_waitlist` and exposes a one-tap "Notify all"
 * button that also flips the `store_v1` feature flag on. Prevents
 * over-sending by defaulting `resend_from_first=false` (skips anyone
 * emailed in the last 24h) — a "Force resend" toggle unlocks a full
 * blast if a launch note needs to go out twice.
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../../components/ui/button';
import { Mail, RefreshCw, Send, ShoppingBag } from 'lucide-react';

export default function AdminStoreWaitlist() {
  const { API } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [broadcasting, setBroadcasting] = useState(false);
  const [forceResend, setForceResend] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/store/waitlist`);
      setRows(data.rows || []);
    } catch (_) {
      toast.error('Could not load waitlist');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const broadcast = async () => {
    if (!window.confirm(
      `Send the launch email to ${rows.length} waitlist members? This will ALSO flip the store_v1 feature flag ON.`
    )) return;
    try {
      setBroadcasting(true);
      const { data } = await axios.post(
        `${API}/admin/store/waitlist/broadcast?resend_from_first=${forceResend}`,
      );
      setLastResult(data);
      if (data.mocked) {
        toast.warning(`Resend key missing — logged ${data.skipped} would-send events. Store flag flipped on.`);
      } else {
        toast.success(`${data.sent} launch emails sent · store live`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Broadcast failed');
    } finally { setBroadcasting(false); }
  };

  return (
    <div className="container mx-auto px-4 sm:px-6 py-8 max-w-5xl" data-testid="admin-store-waitlist-page">
      <div className="flex items-start gap-3 mb-6">
        <div className="h-11 w-11 rounded-xl bg-primary/15 text-primary flex items-center justify-center">
          <ShoppingBag className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Launch</div>
          <h1 className="font-heading text-2xl font-extrabold text-foreground">Store waitlist</h1>
          <p className="text-sm text-muted-foreground">
            {rows.length} member{rows.length === 1 ? '' : 's'} waiting. One tap notifies everyone and flips the store live.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} className="rounded-full" data-testid="admin-waitlist-refresh">
          <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="rounded-2xl border border-primary/40 bg-primary/5 p-4 mb-5">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="font-heading font-extrabold text-base">Launch broadcast</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Emails everyone on the list AND turns on <code className="rounded bg-muted px-1">store_v1</code>. Skips members
              notified in the last 24h unless <b>Force resend</b> is on.
            </div>
            <label className="inline-flex items-center gap-2 mt-2 text-xs font-bold">
              <input
                type="checkbox"
                checked={forceResend}
                onChange={(e) => setForceResend(e.target.checked)}
                data-testid="admin-waitlist-force-resend"
              />
              Force resend to everyone (ignore 24h dedupe)
            </label>
          </div>
          <Button
            onClick={broadcast}
            disabled={broadcasting || rows.length === 0}
            className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="admin-waitlist-broadcast"
          >
            <Send className="w-3.5 h-3.5 mr-1.5" />
            {broadcasting ? 'Sending…' : `Notify ${rows.length} & go live`}
          </Button>
        </div>
        {lastResult && (
          <div className="mt-3 text-[11px] text-foreground/80 grid grid-cols-2 md:grid-cols-4 gap-2">
            <div><b>{lastResult.total_targets}</b> targeted</div>
            <div><b className="text-emerald-700">{lastResult.sent}</b> sent</div>
            <div><b className="text-amber-700">{lastResult.skipped}</b> {lastResult.mocked ? 'logged (no key)' : 'skipped'}</div>
            <div><b className="text-rose-700">{lastResult.failed}</b> failed</div>
          </div>
        )}
      </div>

      {loading && <div className="text-center py-16 text-sm text-muted-foreground">Loading waitlist…</div>}
      {!loading && rows.length === 0 && (
        <div className="text-center py-16 border border-dashed border-border rounded-2xl text-sm text-muted-foreground">
          Nobody's on the waitlist yet.
        </div>
      )}

      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.id} className="rounded-xl border border-border bg-card p-3" data-testid={`admin-waitlist-row-${r.id}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <Mail className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="font-bold text-sm truncate">{r.email}</span>
              {r.interests?.length > 0 && (
                <span className="text-[10px] text-muted-foreground">
                  · {r.interests.join(', ')}
                </span>
              )}
              <span className="text-[10px] text-muted-foreground ml-auto">
                {new Date(r.created_at).toLocaleDateString()}
              </span>
              {r.launch_notified_at && (
                <span className="text-[9px] uppercase tracking-widest font-black bg-emerald-500/15 text-emerald-700 rounded-full px-2 py-0.5">
                  Notified
                </span>
              )}
            </div>
            {r.note && <div className="mt-1 text-xs text-muted-foreground italic pl-6">"{r.note}"</div>}
          </li>
        ))}
      </ul>
    </div>
  );
}
